import { useState, useCallback } from 'react';
import { api, AdminPaymentRecord, AdminPaymentSearchParams } from '@/lib/api';

export function PaymentSearchTab() {
  const [filters, setFilters] = useState<AdminPaymentSearchParams>({ limit: 25 });
  const [payments, setPayments] = useState<AdminPaymentRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  const search = useCallback(
    async (offset = 0) => {
      setLoading(true);
      setError(null);
      try {
        const result = await api.adminSearchPayments({ ...filters, offset });
        setPayments(result.payments);
        setTotal(result.total);
        setFilters((f) => ({ ...f, offset }));
        setSearched(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Search failed');
      } finally {
        setLoading(false);
      }
    },
    [filters],
  );

  const limit = filters.limit ?? 25;
  const offset = filters.offset ?? 0;
  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  const statusBadge = (status: string) => {
    const colors: Record<string, string> = {
      succeeded: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      refunded: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      partially_refunded:
        'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      pending: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    };
    return (
      <span
        className={`px-2 py-0.5 text-xs font-medium rounded-full ${colors[status] || 'bg-muted text-muted-foreground'}`}
      >
        {status}
      </span>
    );
  };

  return (
    <div>
      {/* Filters */}
      <div className="bg-card rounded-lg border border-border p-4 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Status</label>
            <select
              value={filters.status || ''}
              onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value || undefined }))}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
            >
              <option value="">All</option>
              <option value="succeeded">Succeeded</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
              <option value="partially_refunded">Partially Refunded</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">User ID</label>
            <input
              type="text"
              value={filters.userId || ''}
              onChange={(e) => setFilters((f) => ({ ...f, userId: e.target.value || undefined }))}
              placeholder="Filter by user..."
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Start Date</label>
            <input
              type="date"
              value={filters.startDate || ''}
              onChange={(e) =>
                setFilters((f) => ({ ...f, startDate: e.target.value || undefined }))
              }
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">End Date</label>
            <input
              type="date"
              value={filters.endDate || ''}
              onChange={(e) => setFilters((f) => ({ ...f, endDate: e.target.value || undefined }))}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
            />
          </div>
        </div>
        <div className="flex gap-3 items-end">
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Min Amount (cents)</label>
            <input
              type="number"
              value={filters.minAmountCents || ''}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  minAmountCents: e.target.value ? parseInt(e.target.value, 10) : undefined,
                }))
              }
              placeholder="e.g. 100"
              className="w-32 px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Max Amount (cents)</label>
            <input
              type="number"
              value={filters.maxAmountCents || ''}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  maxAmountCents: e.target.value ? parseInt(e.target.value, 10) : undefined,
                }))
              }
              placeholder="e.g. 10000"
              className="w-32 px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
            />
          </div>
          <button
            onClick={() => search(0)}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50"
          >
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
          {error}
        </div>
      )}

      {/* Results */}
      {searched && (
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">
              {total} payment{total !== 1 ? 's' : ''} found
            </h3>
          </div>

          {payments.length === 0 ? (
            <p className="text-sm text-muted-foreground">No payments match your filters</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 text-muted-foreground font-medium">
                        Payment ID
                      </th>
                      <th className="text-left py-2 text-muted-foreground font-medium">User ID</th>
                      <th className="text-left py-2 text-muted-foreground font-medium">Amount</th>
                      <th className="text-left py-2 text-muted-foreground font-medium">Credits</th>
                      <th className="text-left py-2 text-muted-foreground font-medium">Provider</th>
                      <th className="text-left py-2 text-muted-foreground font-medium">Status</th>
                      <th className="text-left py-2 text-muted-foreground font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p.paymentId} className="border-b border-border/50">
                        <td className="py-2 text-foreground font-mono text-xs">{p.paymentId}</td>
                        <td className="py-2 text-foreground font-mono text-xs truncate max-w-[120px]">
                          {p.userId}
                        </td>
                        <td className="py-2 text-foreground font-medium">{p.displayAmount}</td>
                        <td className="py-2 text-muted-foreground">
                          {p.creditsPurchased.toFixed(0)}
                        </td>
                        <td className="py-2 text-muted-foreground">{p.paymentProvider}</td>
                        <td className="py-2">{statusBadge(p.status)}</td>
                        <td className="py-2 text-muted-foreground">
                          {new Date(p.createdAt).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => search(Math.max(0, offset - limit))}
                      disabled={offset === 0 || loading}
                      className="px-3 py-1.5 text-sm bg-muted text-muted-foreground hover:bg-accent rounded-md disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => search(offset + limit)}
                      disabled={offset + limit >= total || loading}
                      className="px-3 py-1.5 text-sm bg-muted text-muted-foreground hover:bg-accent rounded-md disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
